/**
 * Character Library Service
 *
 * Handles Character Builder operations: creating, editing, and managing
 * reusable character cards that can be used across stories and challenges.
 */

import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type {
  LibraryCharacter,
  LibraryCharacterInsert,
  LibraryCharacterUpdate,
} from '@/types/database'

// =============================================================================
// Character Queries
// =============================================================================

/**
 * Get all characters for a child
 */
export async function getCharactersForChild(
  childId: string
): Promise<LibraryCharacter[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('character_library')
    .select('*')
    .eq('child_id', childId)
    .is('deleted_at', null)
    .order('is_favorite', { ascending: false })
    .order('last_used_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching characters:', error)
    throw error
  }

  return (data as LibraryCharacter[]) || []
}

/**
 * Get favorite characters for a child
 */
export async function getFavoriteCharacters(
  childId: string
): Promise<LibraryCharacter[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('character_library')
    .select('*')
    .eq('child_id', childId)
    .eq('is_favorite', true)
    .is('deleted_at', null)
    .order('last_used_at', { ascending: false, nullsFirst: false })

  if (error) {
    console.error('Error fetching favorite characters:', error)
    throw error
  }

  return (data as LibraryCharacter[]) || []
}

/**
 * Get recently used characters for a child
 */
export async function getRecentCharacters(
  childId: string,
  limit: number = 5
): Promise<LibraryCharacter[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('character_library')
    .select('*')
    .eq('child_id', childId)
    .is('deleted_at', null)
    .not('last_used_at', 'is', null)
    .order('last_used_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching recent characters:', error)
    throw error
  }

  return (data as LibraryCharacter[]) || []
}

/**
 * Get a single character by ID
 */
export async function getCharacter(
  characterId: string
): Promise<LibraryCharacter | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('character_library')
    .select('*')
    .eq('id', characterId)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    console.error('Error fetching character:', error)
    throw error
  }

  return data as LibraryCharacter
}

// =============================================================================
// Character Mutations
// =============================================================================

/**
 * Character creation data from the wizard
 */
export interface CharacterWizardData {
  name: string
  avatar_emoji?: string
  traits?: string[]
  wants?: string
  fear?: string
  secret?: string
  signature_item?: string
  voice_sample?: string
  backstory?: string
}

/**
 * Create a new character
 */
export async function createCharacter(
  childId: string,
  data: CharacterWizardData
): Promise<LibraryCharacter> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const insert: LibraryCharacterInsert = {
    child_id: childId,
    name: data.name,
    avatar_emoji: data.avatar_emoji || '👤',
    traits: data.traits || [],
    wants: data.wants || null,
    fear: data.fear || null,
    secret: data.secret || null,
    signature_item: data.signature_item || null,
    voice_sample: data.voice_sample || null,
    backstory: data.backstory || null,
  }

  const { data: result, error } = await supabase
    .from('character_library')
    .insert(insert as Record<string, unknown>)
    .select()
    .single()

  if (error) {
    console.error('Error creating character:', error)
    throw error
  }

  return result as LibraryCharacter
}

/**
 * Update an existing character
 */
export async function updateCharacter(
  characterId: string,
  data: Partial<CharacterWizardData>
): Promise<LibraryCharacter> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const update: LibraryCharacterUpdate = {
    ...(data.name !== undefined && { name: data.name }),
    ...(data.avatar_emoji !== undefined && { avatar_emoji: data.avatar_emoji }),
    ...(data.traits !== undefined && { traits: data.traits }),
    ...(data.wants !== undefined && { wants: data.wants || null }),
    ...(data.fear !== undefined && { fear: data.fear || null }),
    ...(data.secret !== undefined && { secret: data.secret || null }),
    ...(data.signature_item !== undefined && { signature_item: data.signature_item || null }),
    ...(data.voice_sample !== undefined && { voice_sample: data.voice_sample || null }),
    ...(data.backstory !== undefined && { backstory: data.backstory || null }),
    updated_at: new Date().toISOString(),
  }

  const { data: result, error } = await supabase
    .from('character_library')
    .update(update as Record<string, unknown>)
    .eq('id', characterId)
    .select()
    .single()

  if (error) {
    console.error('Error updating character:', error)
    throw error
  }

  return result as LibraryCharacter
}

/**
 * Toggle favorite status
 */
export async function toggleFavorite(
  characterId: string
): Promise<LibraryCharacter> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  // Get current status
  const character = await getCharacter(characterId)
  if (!character) {
    throw new Error('Character not found')
  }

  const { data, error } = await supabase
    .from('character_library')
    .update({
      is_favorite: !character.is_favorite,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', characterId)
    .select()
    .single()

  if (error) {
    console.error('Error toggling favorite:', error)
    throw error
  }

  return data as LibraryCharacter
}

/**
 * Mark character as used (updates last_used_at and increments stories_used_in)
 */
export async function markCharacterUsed(
  characterId: string
): Promise<LibraryCharacter> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  // Get current count
  const character = await getCharacter(characterId)
  if (!character) {
    throw new Error('Character not found')
  }

  const { data, error } = await supabase
    .from('character_library')
    .update({
      last_used_at: new Date().toISOString(),
      stories_used_in: character.stories_used_in + 1,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', characterId)
    .select()
    .single()

  if (error) {
    console.error('Error marking character used:', error)
    throw error
  }

  return data as LibraryCharacter
}

/**
 * Delete a character (soft delete)
 */
export async function deleteCharacter(characterId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const { error } = await supabase
    .from('character_library')
    .update({
      deleted_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', characterId)

  if (error) {
    console.error('Error deleting character:', error)
    throw error
  }
}

/**
 * Duplicate a character
 */
export async function duplicateCharacter(
  characterId: string,
  childId: string
): Promise<LibraryCharacter> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  // Get original character
  const original = await getCharacter(characterId)
  if (!original) {
    throw new Error('Character not found')
  }

  // Create duplicate with modified name
  const insert: LibraryCharacterInsert = {
    child_id: childId,
    name: `${original.name} (copy)`,
    avatar_emoji: original.avatar_emoji,
    traits: original.traits,
    wants: original.wants,
    fear: original.fear,
    secret: original.secret,
    signature_item: original.signature_item,
    voice_sample: original.voice_sample,
    backstory: original.backstory,
    is_favorite: false,
  }

  const { data, error } = await supabase
    .from('character_library')
    .insert(insert as Record<string, unknown>)
    .select()
    .single()

  if (error) {
    console.error('Error duplicating character:', error)
    throw error
  }

  return data as LibraryCharacter
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get character count for a child
 */
export async function getCharacterCount(childId: string): Promise<number> {
  if (!isSupabaseConfigured()) return 0

  const { count, error } = await supabase
    .from('character_library')
    .select('*', { count: 'exact', head: true })
    .eq('child_id', childId)
    .is('deleted_at', null)

  if (error) {
    console.error('Error counting characters:', error)
    return 0
  }

  return count || 0
}

/**
 * Search characters by name
 */
export async function searchCharacters(
  childId: string,
  query: string
): Promise<LibraryCharacter[]> {
  if (!isSupabaseConfigured()) return []
  if (!query.trim()) return getCharactersForChild(childId)

  const { data, error } = await supabase
    .from('character_library')
    .select('*')
    .eq('child_id', childId)
    .is('deleted_at', null)
    .ilike('name', `%${query}%`)
    .order('is_favorite', { ascending: false })
    .order('name', { ascending: true })

  if (error) {
    console.error('Error searching characters:', error)
    throw error
  }

  return (data as LibraryCharacter[]) || []
}

// =============================================================================
// Starter Templates (UI reference data, not stored in DB)
// =============================================================================

export interface CharacterTemplate {
  name: string
  avatar_emoji: string
  traits: string[]
  wants: string
  fear: string
  description: string
}

export const STARTER_TEMPLATES: CharacterTemplate[] = [
  {
    name: 'The Brave Explorer',
    avatar_emoji: '🧭',
    traits: ['curious', 'adventurous', 'determined'],
    wants: 'to discover new places and uncover hidden secrets',
    fear: 'getting lost and never finding the way home',
    description: 'A character who loves adventure and exploring unknown territories.',
  },
  {
    name: 'The Loyal Friend',
    avatar_emoji: '🤝',
    traits: ['kind', 'caring', 'trustworthy'],
    wants: 'to help others and make everyone feel included',
    fear: 'being alone or losing their friends',
    description: 'A character who values friendship above all else.',
  },
  {
    name: 'The Clever Inventor',
    avatar_emoji: '💡',
    traits: ['creative', 'smart', 'persistent'],
    wants: 'to solve problems and create amazing things',
    fear: 'failing when everyone is counting on them',
    description: 'A character who loves building and inventing solutions.',
  },
  {
    name: 'The Shy Artist',
    avatar_emoji: '🎨',
    traits: ['imaginative', 'sensitive', 'thoughtful'],
    wants: 'to express their feelings through their art',
    fear: 'being judged or misunderstood',
    description: 'A quiet character with a rich inner world and creative talents.',
  },
  {
    name: 'The Determined Athlete',
    avatar_emoji: '⚽',
    traits: ['hardworking', 'competitive', 'team-player'],
    wants: 'to win the big game and make their team proud',
    fear: 'letting their teammates down when it matters most',
    description: 'A sporty character who never gives up.',
  },
]

/**
 * Get trait suggestions for the wizard
 */
export const TRAIT_SUGGESTIONS = [
  // Positive traits
  'brave', 'kind', 'curious', 'creative', 'funny',
  'smart', 'caring', 'adventurous', 'determined', 'honest',
  'helpful', 'patient', 'cheerful', 'imaginative', 'loyal',
  // Interesting flaws
  'stubborn', 'shy', 'clumsy', 'impatient', 'forgetful',
  'messy', 'nervous', 'loud', 'quiet', 'perfectionist',
]

/**
 * Get emoji suggestions for avatars
 */
export const AVATAR_EMOJIS = [
  // People
  '👧', '👦', '👩', '👨', '🧒', '👶', '🧑',
  // Fantasy
  '🧙', '🧚', '🦸', '🧛', '🧜', '🧝', '🤴', '👸',
  // Animals
  '🐱', '🐶', '🐰', '🦊', '🐻', '🐼', '🦁', '🐸',
  // Objects/Symbols
  '🤖', '👽', '👻', '🌟', '🔮', '🎭', '🎪', '🏰',
]
